// SettingsIconPane.jsx

import React, { useRef, useState } from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { clearWorkspaceIcon, uploadWorkspaceIcon } from 'services/workspaces/WorkspaceCoverService';

// Components
import Button from 'components/button/Button';
import WorkspaceCover from 'components/workspacecover/WorkspaceCover';

/**
 * SettingsIconPane
 *
 * Renders upload and remove controls for the active workspace icon.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} workspace - Active workspace model.
 * @param {boolean} canEditIcon - Whether the current user can edit workspace icon.
 *
 * @returns {ReactNode} The rendered icon settings pane.
 */
const SettingsIconPane = React.memo(({
    theme,
    workspace,
    canEditIcon,
}) => {

    // ----- CONTEXT ----- //

    const { setActiveWorkspace } = useWorkspace();

    // ----- STATE ----- //

    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    // ----- MEMOS ----- //

    const hasIcon = Boolean(
        String(workspace?.iconPhotoUrl || workspace?.coverPhotoUrl || '').trim()
    );

    // ----- HANDLERS ----- //

    const handleChooseClick = () => {
        if (!canEditIcon || isUploading || isRemoving) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0] || null;
        event.target.value = '';

        if (!file || !workspace?.key) return;

        setErrorMessage('');
        setStatusMessage('Uploading workspace icon...');
        setIsUploading(true);

        try {
            const nextIconPhotoUrl = await uploadWorkspaceIcon({
                workspaceKey: workspace.key,
                file,
                previousIconPath: workspace.iconPhotoUrl || workspace.coverPhotoUrl || null,
            });

            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return {
                    ...previous,
                    iconPhotoUrl: nextIconPhotoUrl,
                };
            });

            setStatusMessage('Workspace icon updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to upload workspace icon.');
            setStatusMessage('');
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveClick = async () => {
        if (!canEditIcon || !workspace?.key || !workspace?.iconPhotoUrl || isUploading || isRemoving) return;

        setErrorMessage('');
        setStatusMessage('Removing workspace icon...');
        setIsRemoving(true);

        try {
            await clearWorkspaceIcon({
                workspaceKey: workspace.key,
                previousIconPath: workspace.iconPhotoUrl || null,
            });

            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return {
                    ...previous,
                    iconPhotoUrl: null,
                };
            });

            setStatusMessage('Workspace icon removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove workspace icon.');
            setStatusMessage('');
        } finally {
            setIsRemoving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header">
                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Icon
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Workspace icon used in the switcher, list rows, and compact headers.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-cover-card" style={{ borderColor: theme.backgroundHover }}>
                    <div className="settings-modal-cover-preview">
                        <WorkspaceCover
                            workspace={workspace}
                            assetType="icon"
                            borderWidth={0}
                            borderRadius={10}
                            size={80}
                            shape={PhotoShape.SQUARE}
                        />
                    </div>

                    <div className="settings-modal-cover-meta">
                        <div className="settings-modal-setting-label" style={{ color: theme.foreground }}>
                            Workspace icon
                        </div>
                        <div className="settings-modal-setting-description" style={{ color: theme.foregroundMuted }}>
                            Images are center-cropped to a square and optimized for compact identity surfaces.
                        </div>
                    </div>

                    <div className="settings-modal-cover-actions">
                        <Button
                            iconKey={hasIcon ? 'image' : 'upload'}
                            label={hasIcon ? 'Replace' : 'Upload'}
                            onClick={handleChooseClick}
                            enabled={canEditIcon && !isUploading && !isRemoving}
                        />

                        <Button
                            iconKey="delete"
                            label="Remove"
                            onClick={handleRemoveClick}
                            enabled={canEditIcon && Boolean(workspace?.iconPhotoUrl) && !isUploading && !isRemoving}
                        />
                    </div>
                </div>

                {!canEditIcon && (
                    <div className="settings-modal-note" style={{ color: theme.foregroundMuted }}>
                        Only admins and owners can update the workspace icon.
                    </div>
                )}

                {(isUploading || isRemoving || statusMessage) && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
        </>
    );
});

// Dev Tools
SettingsIconPane.displayName = 'SettingsIconPane';

export default SettingsIconPane;